import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Flex,
  Input,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { CardPart } from '../chatReducer';
import { buttonState, packBadges } from '../packView';
import type { ApprovalRecord, Fix, Pack } from '../types';
import { ClaimList, NoteSections } from './ClaimList';

const LIST = { margin: '4px 0 8px', paddingLeft: 20 };

/** The gate-1 decision (spec §4 step 7): shown after Approve or Reject. */
const ApprovalView = ({ record }: { record: ApprovalRecord }) => (
  <Alert
    type={record.decision === 'approve' ? 'success' : 'info'}
    showIcon
    message={`${record.decision === 'approve' ? 'Approved' : 'Rejected'} by ${
      record.approver.name ?? record.approver.email
    } (${record.approverRole})`}
    description={
      <div>
        {record.reason && <div>{`Reason: ${record.reason}`}</div>}
        <div>{new Date(record.at).toLocaleString()}</div>
        <div>
          {`Run ${record.runId} · git ${record.gitSha}${
            record.unreviewed ? ' · approved while unreviewed' : ''
          }`}
        </div>
        <div>
          {`Instructions: ${Object.entries(record.instructionsHash)
            .map(([agent, hash]) => `${agent} ${hash}`)
            .join(', ')}`}
        </div>
        {record.demoOnly && (
          <Typography.Text strong>
            Demo only: nothing was saved or sent.
          </Typography.Text>
        )}
      </div>
    }
  />
);

const FixList = ({ title, fixes }: { title: string; fixes: Fix[] }) =>
  fixes.length === 0 ? null : (
    <div>
      <Typography.Text type="secondary">{title}</Typography.Text>
      <ul style={LIST}>
        {fixes.map((fix, i) => (
          <li key={i}>
            <Typography.Text code>{fix.fieldPath}</Typography.Text>{' '}
            {fix.problem}
          </li>
        ))}
      </ul>
    </div>
  );

const TextList = ({ title, items }: { title: string; items: string[] }) =>
  items.length === 0 ? null : (
    <div>
      <Typography.Text type="secondary">{title}</Typography.Text>
      <ul style={LIST}>
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );

type PackCardProps = {
  card: CardPart;
  pack: Pack;
  onAct: (action: 'approve' | 'reject', reason?: string) => void;
  onRunAgain: () => void;
  busy: boolean;
};

/**
 * The pack (spec §10): shortlist, both notes, verdict + fixes, banners,
 * Approve / Reject with a reason / Edit (disabled), then the ApprovalRecord.
 */
const PackCard = ({ card, pack, onAct, onRunAgain, busy }: PackCardProps) => {
  const [pending, setPending] = useState<'approve' | 'reject' | null>(null);
  const sending = card.cardState === 'sending';
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const approve = buttonState(card, 'approve');
  const reject = buttonState(card, 'reject');
  const { draft, notes, review } = pack;

  return (
    <Card
      size="small"
      title={card.title}
      extra={<Tag>{`Round ${pack.rounds}/2`}</Tag>}
    >
      <Flex vertical gap={8}>
        {packBadges(pack).map((badge) => (
          <Alert
            key={badge.text}
            type={badge.type}
            showIcon
            message={badge.text}
          />
        ))}

        {draft && (
          <div>
            <Typography.Title level={5} style={{ marginTop: 0 }}>
              Lender shortlist
            </Typography.Title>
            {draft.shortlist.map((option) => (
              <div key={`${option.rank}-${option.lenderId}`}>
                <Typography.Text strong>
                  {`${option.rank}. ${option.lenderName}${
                    option.productName ? ` – ${option.productName}` : ''
                  }`}
                </Typography.Text>
                <ClaimList
                  title="Rate"
                  claims={option.rate.text ? [option.rate] : []}
                  facts={pack.facts}
                />
                <ClaimList
                  title="Why"
                  claims={option.reasons}
                  facts={pack.facts}
                />
                <ClaimList
                  title="Risks"
                  claims={option.risks}
                  facts={pack.facts}
                />
              </div>
            ))}
            {draft.shortlist.length === 0 && (
              <>
                <Typography.Text type="secondary">
                  No lender shortlisted.
                </Typography.Text>
                <ClaimList
                  title="Why no lender fits"
                  claims={draft.noFit.reasons}
                  facts={pack.facts}
                />
                <TextList
                  title="Gaps that stop a shortlist"
                  items={draft.noFit.gaps}
                />
              </>
            )}
            <ClaimList
              title="Structure"
              claims={draft.structure}
              facts={pack.facts}
            />
            <TextList title="Assumptions" items={draft.assumptions} />
          </div>
        )}

        {notes && (
          <Collapse
            size="small"
            items={[
              {
                key: 'broker',
                label: 'Broker note',
                children: (
                  <NoteSections
                    sections={notes.brokerNote}
                    facts={pack.facts}
                  />
                ),
              },
              {
                key: 'compliance',
                label: 'Compliance note',
                children: (
                  <NoteSections
                    sections={notes.complianceNote}
                    facts={pack.facts}
                  />
                ),
              },
            ]}
          />
        )}

        <div>
          <Typography.Text type="secondary">
            Compliance verdict{' '}
          </Typography.Text>
          {review ? (
            <Tag color={review.verdict === 'pass' ? 'green' : 'orange'}>
              {review.verdict === 'pass' ? 'Pass' : 'Revise'}
            </Tag>
          ) : (
            <Tag>Not reviewed</Tag>
          )}
        </div>
        {review && <FixList title="Fixes asked for" fixes={review.fixes} />}
        <FixList title="Checks found" fixes={pack.checkFixes} />
        <TextList title="Missing inputs" items={pack.gaps} />

        {(approve !== 'hidden' || reject !== 'hidden') && (
          <Flex gap={8} wrap>
            {approve !== 'hidden' && (
              <Button
                type="primary"
                disabled={approve === 'disabled' || sending}
                loading={sending && pending === 'approve'}
                onClick={() => {
                  setPending('approve');
                  onAct('approve');
                }}
              >
                Approve
              </Button>
            )}
            {reject !== 'hidden' && (
              <Button
                danger
                disabled={reject === 'disabled' || sending}
                onClick={() => setRejecting(true)}
              >
                Reject
              </Button>
            )}
            <Tooltip title="Editing the pack comes after the demo.">
              <Button disabled>Edit</Button>
            </Tooltip>
          </Flex>
        )}

        {rejecting && reject !== 'hidden' && card.cardState !== 'closed' && (
          <Flex gap={8}>
            <Input.TextArea
              autoSize={{ minRows: 1, maxRows: 4 }}
              maxLength={1000}
              aria-label="Reason for rejecting"
              placeholder="Why are you rejecting it? (required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button
              danger
              disabled={!reason.trim() || sending}
              loading={sending && pending === 'reject'}
              onClick={() => {
                setPending('reject');
                onAct('reject', reason.trim());
              }}
            >
              Confirm reject
            </Button>
          </Flex>
        )}

        {card.approval && <ApprovalView record={card.approval} />}
        {card.chosen === 'reject' && (
          <Button
            style={{ alignSelf: 'flex-start' }}
            disabled={busy}
            onClick={onRunAgain}
          >
            Run again
          </Button>
        )}
        {card.cardError && (
          <Alert type="error" showIcon message={card.cardError} />
        )}
      </Flex>
    </Card>
  );
};

export default PackCard;
